import { AppLayout } from "@/components/AppLayout";
import { Zap, Plus, History, TrendingUp, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useNavigate } from "react-router-dom";
import { useCredits } from "@/hooks/useCredits";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

const creditPacks = [
  { credits: 50, price: "4 900 FCFA", popular: false },
  { credits: 150, price: "12 500 FCFA", popular: true, savings: "15%" },
  { credits: 350, price: "24 900 FCFA", popular: false, savings: "28%" },
  { credits: 750, price: "59 900 FCFA", popular: false, savings: "18%" },
];

const Credits = () => {
  const navigate = useNavigate();
  const { balance, transactions, creditsLoading, transactionsLoading, addCredits } = useCredits();
  const maxCredits = 500;

  // Calculate stats from transactions
  const thisMonthUsage = transactions
    .filter(t => t.type === 'usage' && new Date(t.created_at).getMonth() === new Date().getMonth())
    .reduce((acc, t) => acc + Math.abs(t.amount), 0);

  const thisMonthGenerations = transactions
    .filter(t => t.type === 'usage' && new Date(t.created_at).getMonth() === new Date().getMonth())
    .length;

  const proGenerations = transactions
    .filter(t => t.type === 'usage' && typeof t.metadata === 'object' && t.metadata !== null && 'model' in t.metadata && t.metadata.model === 'floot-pro')
    .length;
  
  const totalGenerations = transactions.filter(t => t.type === 'usage').length;
  const proPercentage = totalGenerations > 0 ? Math.round((proGenerations / totalGenerations) * 100) : 0;

  const handleBuyCredits = () => {
    navigate("/payment");
  };

  const formatTransactionDate = (date: string) => {
    const d = new Date(date);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return "Hier";
    return formatDistanceToNow(d, { addSuffix: true, locale: fr });
  };

  const getTransactionLabel = (t: typeof transactions[0]) => {
    switch (t.type) {
      case 'purchase': return 'Achat de crédits';
      case 'bonus': return 'Bonus';
      case 'refund': return 'Remboursement';
      case 'usage': return t.description;
      default: return t.description;
    }
  };

  return (
    <AppLayout title="Crédits">
      <div className="p-4 md:p-6 space-y-6 md:space-y-8">
        {/* Current Balance */}
        <div className="bg-gradient-to-br from-primary/20 via-violet/20 to-accent/20 border border-primary/30 rounded-2xl p-4 md:p-6">
          <div className="flex flex-col gap-4 md:gap-6">
            <div className="space-y-3 md:space-y-4">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 md:w-6 md:h-6 text-highlight" />
                <span className="text-base md:text-lg font-semibold text-foreground">Mes crédits Floop</span>
              </div>
              {creditsLoading ? (
                <Skeleton className="h-10 md:h-12 w-24" />
              ) : (
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl md:text-5xl font-bold text-foreground">{balance}</span>
                  <span className="text-sm md:text-base text-muted-foreground">crédits</span>
                </div>
              )}
              <div className="space-y-2">
                <Progress value={(balance / maxCredits) * 100} className="h-2 w-full max-w-64" />
                <p className="text-xs md:text-sm text-muted-foreground">
                  {balance} / {maxCredits} crédits
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate("/pricing")}
              className="bg-gradient-floop hover:opacity-90 text-white font-semibold px-6 py-5 md:px-8 md:py-6 rounded-xl w-full md:w-auto"
            >
              <Plus className="w-4 h-4 md:w-5 md:h-5 mr-2" />
              Acheter des crédits
            </Button>
          </div>
        </div>

        {/* Quick Buy */}
        <div className="space-y-3 md:space-y-4">
          <h2 className="text-lg md:text-xl font-semibold text-foreground">Recharger rapidement</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {creditPacks.map((pack) => (
              <button
                key={pack.credits}
                onClick={handleBuyCredits}
                className={`relative p-4 md:p-6 rounded-2xl border transition-all hover:scale-105 ${
                  pack.popular
                    ? "bg-gradient-to-br from-primary/20 to-accent/20 border-primary/50"
                    : "bg-card border-border hover:border-primary/30"
                }`}
              >
                {pack.popular && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-gradient-floop text-white text-xs font-semibold rounded-full">
                    Populaire
                  </span>
                )}
                <div className="text-center space-y-1 md:space-y-2">
                  <div className="flex items-center justify-center gap-1">
                    <Zap className="w-4 h-4 md:w-5 md:h-5 text-highlight" />
                    <span className="text-xl md:text-2xl font-bold text-foreground">{pack.credits}</span>
                  </div>
                  <span className="text-xs md:text-sm text-muted-foreground">crédits</span>
                  <div className="text-sm md:text-xl font-semibold text-foreground">{pack.price}</div>
                  {pack.savings && (
                    <span className="text-xs text-success">-{pack.savings}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Usage Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
          <div className="bg-card border border-border rounded-2xl p-4 md:p-6">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-primary" />
              <span className="text-xs md:text-sm text-muted-foreground">Ce mois</span>
            </div>
            {transactionsLoading ? (
              <Skeleton className="h-6 md:h-8 w-12" />
            ) : (
              <>
                <div className="text-2xl md:text-3xl font-bold text-foreground">{thisMonthGenerations}</div>
                <span className="text-xs md:text-sm text-muted-foreground">générations</span>
              </>
            )}
          </div>
          <div className="bg-card border border-border rounded-2xl p-4 md:p-6">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 md:w-5 md:h-5 text-highlight" />
              <span className="text-xs md:text-sm text-muted-foreground">Crédits utilisés</span>
            </div>
            {transactionsLoading ? (
              <Skeleton className="h-6 md:h-8 w-12" />
            ) : (
              <>
                <div className="text-2xl md:text-3xl font-bold text-foreground">{thisMonthUsage}</div>
                <span className="text-xs md:text-sm text-muted-foreground">ce mois</span>
              </>
            )}
          </div>
          <div className="bg-card border border-border rounded-2xl p-4 md:p-6">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-4 h-4 md:w-5 md:h-5 text-violet" />
              <span className="text-xs md:text-sm text-muted-foreground">Modèle favori</span>
            </div>
            {transactionsLoading ? (
              <Skeleton className="h-6 w-24" />
            ) : (
              <>
                <div className="text-lg md:text-xl font-bold text-foreground">
                  {proPercentage >= 50 ? "Floop Pro" : "Floop Basic"}
                </div>
                <span className="text-xs md:text-sm text-muted-foreground">
                  {proPercentage >= 50 ? proPercentage : 100 - proPercentage}% des générations
                </span>
              </>
            )}
          </div>
        </div>

        {/* History */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-muted-foreground" />
            <h2 className="text-xl font-semibold text-foreground">Historique d'utilisation</h2>
          </div>
          <div className="bg-card border border-border rounded-2xl divide-y divide-border">
            {transactionsLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between p-4">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                  <Skeleton className="h-4 w-16" />
                </div>
              ))
            ) : transactions.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                Aucune transaction pour le moment
              </div>
            ) : (
              transactions.slice(0, 10).map((item) => (
                <div key={item.id} className="flex items-center justify-between p-4">
                  <div>
                    <div className="font-medium text-foreground">{getTransactionLabel(item)}</div>
                    <div className="text-sm text-muted-foreground">{formatTransactionDate(item.created_at)}</div>
                  </div>
                  <div
                    className={`font-semibold ${
                      item.amount > 0 ? "text-success" : "text-foreground"
                    }`}
                  >
                    {item.amount > 0 ? "+" : ""}{item.amount} crédits
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default Credits;
